import { isLiquidGlassSupported } from "@-/com.npmjs.callstack__liquid-glass";
import { cn } from "@-/com.npmjs.cn";
import { View } from "@-/com.npmjs.react-native";
import {
  SETTINGS_PREMIUM_SUPERWALL_PLACEMENT,
  useUpgradeToPremium,
} from "@-/dev.improve.haniverse.components.superwall";
import { Button } from "@-/dev.improve.haniverse.components.ui.button";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { ToolbarGlassSurface } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import type { ComponentProps } from "react";
import { LevelsPackDescriptionContent } from "#levels-pack-description-header";

/**
 * The upgrade card pinned above premium-locked levels. It is a glass capsule rather than an opaque
 * card so the blurred levels scrolling beneath it stay faintly visible — the point of letting free
 * learners scroll a locked grade is to see how much is waiting behind it.
 */
export function LevelsPremiumUpgradePrompt({
  onReturn,
  packDescription,
  returnLabel,
}: {
  onReturn?: () => void;
  packDescription?: ComponentProps<typeof LevelsPackDescriptionContent>;
  /** Label of the secondary "go back to what you can use" action; hidden when omitted. */
  returnLabel?: string;
}) {
  // Explicit upgrade buttons share the campaign's configured plans placement. The learning gate
  // placement is separate and may not have a paywall assigned.
  const { errorMessage, isLoading, isPaywallPresented, openUpgradeToPremium } = useUpgradeToPremium(
    SETTINGS_PREMIUM_SUPERWALL_PLACEMENT,
    "levels_list",
  );

  return (
    <ToolbarGlassSurface
      className={cn(
        "gap-3 rounded-xs p-4",
        !isLiquidGlassSupported && "border border-border bg-background shadow-lg",
      )}
      interactive={false}
      testID="levels-free-tier-upgrade-prompt"
    >
      {packDescription === undefined ? null : (
        <View testID="levels-pack-description">
          <LevelsPackDescriptionContent {...packDescription} />
        </View>
      )}
      <View className="flex-row items-center gap-3">
        <View className="size-11 items-center justify-center rounded-full bg-primary/10">
          <Icon className="text-primary" name="MdLockOutline" size={22} />
        </View>
        <View className="min-w-0 flex-1 gap-0.5">
          <Text className="text-base font-semibold text-foreground">
            These levels are part of Premium
          </Text>
          <Text className="text-sm text-muted-foreground">
            Keep scrolling to peek at what’s ahead.
          </Text>
        </View>
      </View>
      <Button
        className="rounded-full"
        disabled={isPaywallPresented}
        isLoading={isLoading}
        onPress={openUpgradeToPremium}
        size="lg"
        testID="upgrade-levels-to-premium-button"
      >
        <Text className="font-semibold">
          {isLoading ? "Loading plans…" : "Unlock all level packs"}
        </Text>
      </Button>
      {errorMessage === null ? null : (
        <Text accessibilityRole="alert" className="text-center text-sm text-destructive">
          {errorMessage}
        </Text>
      )}
      {returnLabel === undefined || onReturn === undefined ? null : (
        <Button
          className="-mt-1 rounded-full"
          onPress={onReturn}
          size="sm"
          testID="return-to-last-accessible-level"
          variant="ghost"
        >
          <Text className="text-muted-foreground">{returnLabel}</Text>
        </Button>
      )}
    </ToolbarGlassSurface>
  );
}
